import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';
import { applyTemplateNow } from '../../utils/templateApply';
import { Button } from '../ui/Button';

// The daily check-in — the one place the app asks you anything.
//
// It shows one question at a time, and the list it draws from is whatever the
// backend still genuinely doesn't know (services/interview.py). Everything the
// wearables reported, the weather providers fetched, or your traits already
// answered never appears here at all, which is why the list shrinks over time
// rather than growing.
//
// Three answers are always available and they mean different things: a value,
// "nothing to report" (real data — it puts the day in the days-without pile),
// and "skip" (recorded, never counted as a measurement). Ratings deliberately
// get no "nothing" button; there is no zero mood.

const todayStr = () => localToday();

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2"
       strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DailyInterview = ({ refreshKey = 0, defaultDate, onLogged }) => {
  const day = defaultDate || todayStr();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [dumpOpen, setDumpOpen] = useState(false);
  const [dumpText, setDumpText] = useState('');

  const question = data?.questions?.[0] || null;

  const load = useCallback(async () => {
    try {
      const { data: body } = await apiClient.get('/api/interview/today', {
        params: { day },
      });
      setData(body);
    } catch {
      setData(null);
    }
  }, [day]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Each question gets a fresh draft seeded to the middle of its range, so a
  // slider never opens pinned at an extreme the user didn't choose.
  useEffect(() => {
    if (!question) { setDraft(null); return; }
    if (question.control === 'number' && question.min != null) {
      setDraft(Math.round((question.min + question.max) / 2 / (question.step || 1))
        * (question.step || 1));
    } else if (question.control === 'time') {
      setDraft('19:00');
    } else {
      setDraft(null);
    }
  }, [question]);

  const send = async (payload) => {
    setBusy(true);
    try {
      const { data: body } = await apiClient.post('/api/interview/answer', payload,
        { params: { day } });
      setData((prev) => ({ ...prev, ...body }));
      onLogged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that');
    } finally {
      setBusy(false);
    }
  };

  const answer = (value) => send({ field: question.field, value, action: 'answer' });
  const nothing = () => send({ field: question.field, action: 'zero' });
  const skip = () => send({ field: question.field, action: 'decline' });

  const sweep = async () => {
    setBusy(true);
    try {
      const { data: body } = await apiClient.post('/api/interview/nothing-else',
        null, { params: { day } });
      setData((prev) => ({ ...prev, ...body }));
      onLogged?.();
    } catch {
      toast.error('Could not save that');
    } finally {
      setBusy(false);
    }
  };

  const submitDump = async () => {
    if (!dumpText.trim()) return;
    setBusy(true);
    try {
      const { data: body } = await apiClient.post('/api/interview/brain-dump',
        { text: dumpText, day });
      const count = Object.keys(body.captured || {}).length;
      toast.success(count ? `Caught ${count} thing${count === 1 ? '' : 's'}`
        : 'Nothing I could pin down in that');
      setDumpText('');
      setDumpOpen(false);
      setData((prev) => ({ ...prev, ...body.plan }));
      onLogged?.();
    } catch {
      toast.error('Could not read that');
    } finally {
      setBusy(false);
    }
  };

  // The one moment a mined routine may speak here: the user just hand-logged
  // the pattern yet again (the backend only sends a suggestion on such a day).
  const suggestion = data?.routine_suggestion || null;

  const acceptSuggestion = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/templates/suggestions/${suggestion.id}/accept`);
      toast.success('Saved to your routines — log it by name, or star it under Me › Checklist.');
      await load();
    } catch {
      toast.error('Could not save that routine');
    } finally {
      setBusy(false);
    }
  };

  const dismissSuggestion = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/templates/suggestions/${suggestion.id}/dismiss`);
      await load();
    } catch {
      toast.error('Could not dismiss that');
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    try {
      await apiClient.post('/api/interview/complete', { day });
      load();
    } catch { /* a failed complete is not worth interrupting anyone over */ }
  };

  if (!data) return null;

  const total = data.core_total || 0;
  const answered = data.answered || 0;
  const setupMissing = data.setup?.missing_essential?.length || 0;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-surface-dark-border
                    bg-white dark:bg-surface-dark-raised p-4">
      {/* Header — progress and streak, both at full size and full contrast. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Today's check-in
          </h2>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {answered} of {total}
          </span>
        </div>
        {data.streak > 0 && (
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {data.streak} day{data.streak === 1 ? '' : 's'} in a row
          </span>
        )}
      </div>

      {/* The trait nudge. Answering these once permanently removes questions, so
          it earns a place above the queue rather than being buried in settings. */}
      {setupMissing > 0 && (
        <div className="mt-3 rounded-lg border border-gray-200 dark:border-surface-dark-border
                        bg-gray-50 dark:bg-surface-dark p-3">
          <p className="text-sm text-gray-800 dark:text-gray-200">
            {setupMissing} quick question{setupMissing === 1 ? '' : 's'} about you,
            answered once — after that we stop asking about them every day.
          </p>
          <a href="/me/traits"
             className="mt-2 inline-block text-sm font-medium text-primary-700 dark:text-primary-400 underline">
            Set these up
          </a>
        </div>
      )}

      {/* An unplaceable location silently blanks weather, daylight and air
          quality — about fifteen columns — while everything else looks fine.
          It gets said out loud for exactly that reason. */}
      {data.location && !data.location.resolved && (
        <div className="mt-3 rounded-lg border border-gray-200 dark:border-surface-dark-border
                        bg-gray-50 dark:bg-surface-dark p-3">
          <p className="text-sm text-gray-800 dark:text-gray-200">
            {data.location.note}
          </p>
          <a href="/me/health"
             className="mt-2 inline-block text-sm font-medium text-primary-700 dark:text-primary-400 underline">
            {data.location.location ? 'Fix your location' : 'Add your location'}
          </a>
        </div>
      )}

      {question ? (
        <div className="mt-4">
          <p className="text-base font-medium text-gray-900 dark:text-gray-100">
            {question.prompt}
          </p>

          {question.control === 'toggle' && (
            <div className="mt-3 flex gap-2">
              <Button onClick={() => answer(true)} disabled={busy}>Yes</Button>
              <Button variant="outline" onClick={() => answer(false)} disabled={busy}>
                No
              </Button>
            </div>
          )}

          {question.control === 'choice' && (
            <div className="mt-3 flex flex-wrap gap-2">
              {question.choices.map((choice) => (
                <Button key={choice} variant="outline" disabled={busy}
                        onClick={() => answer(choice)}>
                  {choice}
                </Button>
              ))}
            </div>
          )}

          {question.control === 'number' && (
            <div className="mt-3">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {draft ?? question.min}
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {question.unit}
                </span>
              </div>
              <input
                type="range"
                min={question.min ?? 0}
                max={question.max ?? 10}
                step={question.step ?? 1}
                value={draft ?? question.min ?? 0}
                onChange={(e) => setDraft(Number(e.target.value))}
                className="mt-2 w-full accent-primary-600"
              />
              <Button className="mt-2" onClick={() => answer(draft)} disabled={busy}>
                Save
              </Button>
            </div>
          )}

          {question.control === 'time' && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                type="time"
                value={draft ?? '19:00'}
                onChange={(e) => setDraft(e.target.value)}
                className="h-10 rounded-md border border-gray-300 dark:border-surface-dark-border
                           bg-white dark:bg-surface-dark px-3 text-base
                           text-gray-900 dark:text-gray-100"
              />
              <Button onClick={() => answer(draft)} disabled={busy}>Save</Button>
            </div>
          )}

          {question.control === 'text' && (
            <div className="mt-3">
              <textarea
                rows={2}
                value={draft ?? ''}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-surface-dark-border
                           bg-white dark:bg-surface-dark p-2 text-base
                           text-gray-900 dark:text-gray-100"
              />
              <Button className="mt-2" onClick={() => answer(draft)} disabled={busy}>
                Save
              </Button>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            {/* "Nothing" is a real answer and says so. Absent for ratings, where
                a zero would be a fiction rather than a fact. */}
            {question.zeroable && (
              <button onClick={nothing} disabled={busy}
                      className="text-sm font-medium text-gray-900 dark:text-gray-100 underline">
                None today
              </button>
            )}
            <button onClick={skip} disabled={busy}
                    className="text-sm text-gray-700 dark:text-gray-300 underline">
              Skip
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <p className="flex items-center gap-2 text-base font-medium text-gray-900 dark:text-gray-100">
            <Check /> That's everything for today.
          </p>
          {data.coverage && (
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
              We know {data.coverage.known} of {data.coverage.total} things about
              today — most of it without asking.
            </p>
          )}
          {/* The no-silent-demotion rule: a day that ended early because of a
              setting must never read as a day that was fully answered, and the
              way back is one tap from here. */}
          {data.set_aside > 0 && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              {data.set_aside} more question{data.set_aside === 1 ? '' : 's'} set
              aside by your {data.thoroughness} setting.{' '}
              <Link to="/me/insights" className="underline font-medium">
                Ask me everything
              </Link>
            </p>
          )}
          <Button className="mt-3" variant="outline" onClick={finish}>Done</Button>
        </div>
      )}

      {/* One-pass description. The fast path for anyone who'd rather talk than tap. */}
      {question && (
        <div className="mt-4 border-t border-gray-200 dark:border-surface-dark-border pt-3">
          {dumpOpen ? (
            <div>
              <textarea
                rows={3}
                autoFocus
                value={dumpText}
                onChange={(e) => setDumpText(e.target.value)}
                placeholder="Slept badly, coffee at 7, walked at lunch, two beers…"
                className="w-full rounded-md border border-gray-300 dark:border-surface-dark-border
                           bg-white dark:bg-surface-dark p-2 text-base
                           text-gray-900 dark:text-gray-100"
              />
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={submitDump} disabled={busy || !dumpText.trim()}>
                  Read it
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDumpOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setDumpOpen(true)}
                      className="text-sm font-medium text-gray-900 dark:text-gray-100 underline">
                Describe the whole day instead
              </button>
              {data.questions.some((q) => q.zeroable) && (
                <button onClick={sweep} disabled={busy}
                        className="text-sm text-gray-700 dark:text-gray-300 underline">
                  Nothing else happened
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* The contextual routine offer — appears only on a day the user just
          hand-logged a pattern the miner has drafted, i.e. exactly when saying
          yes would have saved them the typing they just did. Declining is
          remembered; the same pattern is never offered again. */}
      {suggestion && (
        <div className="mt-4 border-t border-gray-200 dark:border-surface-dark-border pt-3">
          <p className="text-sm text-gray-800 dark:text-gray-200">
            You've logged this {suggestion.seen_days} times lately — make{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {suggestion.icon ? `${suggestion.icon} ` : ''}{suggestion.name}
            </span>{' '}
            a one-tap routine?
          </p>
          <div className="mt-2 flex items-center gap-3">
            <Button size="sm" onClick={acceptSuggestion} disabled={busy}>
              Make it a routine
            </Button>
            <button onClick={dismissSuggestion} disabled={busy}
                    className="text-sm text-gray-700 dark:text-gray-300 underline">
              No thanks
            </button>
          </div>
        </div>
      )}

      {/* Pinned routines — one-tap logging shortcuts, never questions. */}
      {data.routines?.length > 0 && (
        <div className="mt-4 border-t border-gray-200 dark:border-surface-dark-border pt-3">
          <div className="flex flex-wrap gap-2">
            {data.routines.map((routine) => (
              <Button
                key={routine.id}
                size="sm"
                variant={routine.done ? 'secondary' : 'outline'}
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { count } = await applyTemplateNow({
                      id: routine.template_id,
                      name: routine.label,
                      items: routine.items || [],
                    }, day);
                    toast[count ? 'success' : 'error'](
                      count ? `${routine.label} logged`
                            : 'That routine has nothing to log yet.');
                    await load();
                    onLogged?.();
                  } catch {
                    toast.error('Could not log that');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {routine.icon ? `${routine.icon} ` : ''}{routine.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyInterview;
